import { useEffect, useLayoutEffect, useMemo, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { ErrorBoundary } from './ErrorBoundary';
import { StoreProvider, useStores } from '@/stores/context';
import { RootStore } from '@/stores/RootStore';
// Deep import: the `@/features/auth` barrel re-exports LoginPage and SetupPage,
// so reaching it from the always-loaded provider tree defeats the lazy routes
// below and puts both screens (and every form primitive they use) in the entry
// chunk. The context provider itself is a dozen lines.
import { AuthStoreProvider } from '@/features/auth/AuthStoreProvider';
import { adaptConsoleTheme, themeApi } from '@/api/theme';
import { Toaster } from '@/components/ui/sonner';
import { showStatusToast } from '@/components/pos/status-toast';
import { enablePush, isPushEnabled, readPushSupport, syncPush } from '@/pwa/push';

/**
 * Global providers — owned by the integration lead only.
 * Provider order: ErrorBoundary → StoreProvider → AuthStoreProvider →
 * theme runtime (ThemeSync/AppToaster) → routes.
 */

/**
 * Org theme runtime (V1-01/V1-02 integration): after login + organization
 * selection, fetch the raw /pos/theme response and apply the validated
 * primary. Any failure or invalid color falls back to the default brand —
 * theming must never break the console (01-architecture.md §6).
 */
const ThemeSync = observer(function ThemeSync() {
  const { auth, theme } = useStores();
  // Hydration first: /theme is a protected read, and firing it before the
  // persisted token is in the store buys a 401 that purges the session.
  const organization = auth.hydrated && auth.isAuthenticated ? auth.organization : null;

  useEffect(() => {
    if (!organization) {
      theme.setPrimary(null);
      return;
    }
    const controller = new AbortController();
    themeApi
      .getRaw({ signal: controller.signal })
      .then((raw) => {
        const adapted = adaptConsoleTheme({ theme: raw, organization });
        theme.setPrimary(adapted.primary_color);
      })
      .catch(() => {
        theme.setPrimary(null);
      });
    return () => controller.abort();
  }, [organization, theme]);

  return null;
});

const AppToaster = observer(function AppToaster() {
  const { theme } = useStores();
  return <Toaster theme={theme.isDark ? 'dark' : 'light'} />;
});

/** Pick up platform channel/role changes when a workstation is used again. */
const AccessSync = observer(function AccessSync() {
  const { auth, session } = useStores();
  const orgId = auth.isAuthenticated ? auth.organization?.id : null;
  const terminalId = auth.terminal?.id;
  useEffect(() => {
    if (orgId != null && !auth.reconciled) void auth.reconcile();
  }, [auth, orgId]);

  useEffect(() => {
    if (orgId == null) return;
    const refresh = () => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) return;
      void auth.reconcile();
      if (terminalId != null && session.currentStatus !== 'loading') void session.loadCurrent();
    };
    // Returning to a hidden tab needs fresh access/session data. Focusing a
    // window (including switching to and from DevTools) does not.
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('online', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [auth, session, orgId, terminalId]);
  return null;
});

/**
 * Keep an opted-in browser subscribed without making the user revisit
 * Settings, and surface the one click browsers require on a new device.
 */
const PushSync = observer(function PushSync() {
  const { auth } = useStores();
  const orgId = auth.hydrated && auth.isAuthenticated ? (auth.organization?.id ?? null) : null;
  const userId = auth.hydrated && auth.isAuthenticated ? (auth.user?.id ?? null) : null;
  const branchId = auth.hydrated && auth.isAuthenticated ? (auth.branch?.id ?? null) : null;

  useEffect(() => {
    if (orgId === null || !isPushEnabled()) return;

    let disposed = false;
    const repair = () => {
      if (!disposed) void syncPush();
    };
    const repairWhenVisible = () => {
      if (document.visibilityState === 'visible') repair();
    };

    repair();
    window.addEventListener('online', repair);
    window.addEventListener('focus', repair);
    document.addEventListener('visibilitychange', repairWhenVisible);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', repair);
      void navigator.serviceWorker.ready.then(repair).catch(() => undefined);
    }

    const cleanup = () => {
      disposed = true;
      window.removeEventListener('online', repair);
      window.removeEventListener('focus', repair);
      document.removeEventListener('visibilitychange', repairWhenVisible);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('controllerchange', repair);
      }
    };

    const support = readPushSupport();
    if (!support.supported || support.permission !== 'default') return cleanup;

    const promptKey = `sg.push.prompted:${orgId}`;
    try {
      if (sessionStorage.getItem(promptKey) === '1') return cleanup;
      sessionStorage.setItem(promptKey, '1');
    } catch {
      // A private window may deny storage; one prompt is still preferable to silence.
    }

    showStatusToast({
      title: 'Turn on order notifications',
      body: 'Get new-order alerts with a bell on this phone or computer.',
      duration: 12_000,
      action: {
        label: 'Enable',
        onClick: () => {
          void enablePush()
            .then(() => {
              showStatusToast({
                title: 'Order notifications on',
                body: 'This device will alert you for new orders.',
              });
            })
            .catch((error: unknown) => {
              showStatusToast({
                tone: 'danger',
                title: 'Could not enable notifications',
                body:
                  error instanceof Error ? error.message : 'Check browser notification settings.',
              });
            });
        },
      },
    });
    return cleanup;
  }, [branchId, orgId, userId]);

  return null;
});

/**
 * Realtime lifecycle (V1-05 integration): connect to the existing Reverb
 * channel after login + organization selection; reconnect on org change;
 * disconnect on logout/unmount. With no Reverb env the store stays in
 * polling mode by design (06-realtime.md §2).
 */
const RealtimeSync = observer(function RealtimeSync() {
  const { auth, orders, realtime, session } = useStores();
  const orgId = auth.hydrated && auth.isAuthenticated ? (auth.organization?.id ?? null) : null;
  const terminalId = auth.terminal?.id ?? null;

  // Notifications are application-wide, so resolving the active-order scope
  // cannot depend on visiting /pos/queue first. Without this, the socket was
  // connected on Office → Orders but OrderStore held reconciliation until the
  // queue route supplied a shift, and no new-order chime/toast could fire.
  useEffect(() => {
    if (orgId !== null && terminalId !== null && session.currentStatus === 'idle') {
      void session.loadCurrent();
    }
  }, [orgId, session, session.currentStatus, terminalId]);

  const daySessionId = session.isDayOpen ? (session.currentSession?.id ?? null) : null;
  const daySessionResolved =
    terminalId === null || session.currentStatus === 'loaded' || session.currentStatus === 'error';
  const storeResolved = orders.daySessionResolved;

  useEffect(() => {
    if (orgId !== null) orders.setDaySessionId(daySessionId, daySessionResolved);
  }, [daySessionId, daySessionResolved, orders, orgId, storeResolved]);

  useEffect(() => {
    if (orgId == null) {
      realtime.disconnect();
      return;
    }
    realtime.connect(orgId);
    return () => realtime.disconnect();
  }, [orgId, realtime]);

  return null;
});

export function AppProviders({ children, store }: { children: ReactNode; store?: RootStore }) {
  // AuthStore restores the persisted login in its own constructor, so the
  // store handed down here is already hydrated (see AuthStore).
  const root = useMemo(() => store ?? new RootStore(), [store]);

  /**
   * Bind the COMMITTED store to the shared axios clients.
   *
   * AuthStore attaches itself on construction, and the client keeps one
   * module-level provider — "last store attached wins". React 19 StrictMode
   * double-invokes useMemo factories, so development built two RootStores and
   * the interceptor could be left holding the one React discarded: the UI read
   * a store with a token while every request was signed by a store without
   * one, producing 401 "Unauthenticated." under a header saying "Signed in".
   * The 401 handler then declined to purge, because ITS store had no token to
   * purge, so the error just sat there with a Retry button.
   *
   * Effects only run for the tree React kept, so re-attaching here makes the
   * observed store and the requesting store the same object by construction.
   */
  useLayoutEffect(() => {
    root.auth.attachToApi();
  }, [root]);

  return (
    <ErrorBoundary>
      <StoreProvider store={root}>
        <AuthStoreProvider store={root.auth}>
          <ThemeSync />
          <AccessSync />
          <RealtimeSync />
          <PushSync />
          {children}
          <AppToaster />
        </AuthStoreProvider>
      </StoreProvider>
    </ErrorBoundary>
  );
}
